import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Drawer, Button, Input } from 'antd';
import { CustomerServiceOutlined, SendOutlined, RobotOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { sendChatMessage } from '../../api/chat';
import './index.css';

const { TextArea } = Input;

// ══════════════════════════════════════════════
//  常量
// ══════════════════════════════════════════════
const MAX_CHARS = 2000;
const MAX_ROUNDS = 50;

// ══════════════════════════════════════════════
//  Markdown 渲染器（轻量）
// ══════════════════════════════════════════════
const renderMarkdown = (text) => {
    if (!text) return text;

    // 转义 HTML 防止 XSS
    let html = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    // 代码块（```...```）
    html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_, lang, code) => {
        return `<pre><code>${code.trim()}</code></pre>`;
    });

    // 行内代码（`code`）
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

    // 标题
    html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
    html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
    html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');

    // 加粗
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

    // 链接
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

    // 无序列表（- 或 *）
    html = html.replace(/^\s*[-*]\s(.+)$/gm, '<li>$1</li>');

    // 有序列表
    html = html.replace(/^\s*\d+\.\s(.+)$/gm, '<li>$1</li>');

    // 包裹连续的 <li> 为 <ul>（简化：无序列表用 ul，有序列表同样用 ul 保证出块）
    html = html.replace(/((?:<li>.*?<\/li>\n?)+)/g, '<ul>$1</ul>');

    // 引用
    html = html.replace(/^&gt;\s?(.+)$/gm, '<blockquote>$1</blockquote>');

    // 段落（连续的普通文本用 <p> 包裹）
    // 先清理已有的 HTML 块级标签，剩余文本按双换行分段
    const paragraphs = html.split(/\n\n+/);
    html = paragraphs
        .map((p) => {
            const trimmed = p.trim();
            if (!trimmed) return '';
            // 如果已经包含块级 HTML 标签，不包裹
            if (/^<(h[1-3]|ul|ol|pre|blockquote|li)/.test(trimmed)) {
                return trimmed;
            }
            return `<p>${trimmed.replace(/\n/g, '<br/>')}</p>`;
        })
        .join('\n');

    return html;
};

// ══════════════════════════════════════════════
//  欢迎页
// ══════════════════════════════════════════════
const WelcomePanel = () => (
    <div className="chat-welcome">
        <div className="chat-welcome-avatar">
            <RobotOutlined />
        </div>
        <div className="chat-welcome-text">
            你好，我是鹤梦云 AI 助手，有什么可以帮助你的？
        </div>

    </div>
);

// ══════════════════════════════════════════════
//  消息气泡
// ══════════════════════════════════════════════
const MessageBubble = ({ message, onRetry }) => {
    if (message.role === 'system') {
        return (
            <div className="chat-system-notice">{message.content}</div>
        );
    }

    const isUser = message.role === 'user';
    const isError = message.status === 'error';

    const bubbleClass = [
        'chat-bubble',
        isUser ? 'chat-bubble-user' : isError ? 'chat-bubble-error' : 'chat-bubble-ai',
    ].join(' ');

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start' }}>
            {/* AI 头像 */}
            {!isUser && !isError && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <div style={{
                        width: 24, height: 24, borderRadius: '50%',
                        background: 'linear-gradient(135deg, #1677ff 0%, #69b1ff 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 14, color: '#fff',
                    }}>
                        <RobotOutlined />
                    </div>
                </div>
            )}
            {/* 错误图标 */}
            {isError && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, paddingLeft: 4 }}>
                    <ExclamationCircleOutlined style={{ color: '#cf1322', fontSize: 16 }} />
                </div>
            )}
            <div className={bubbleClass}>
                {isUser || isError ? (
                    message.content
                ) : (
                    <div
                        className="chat-markdown"
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(message.content) }}
                    />
                )}
                {isError && (
                    <div className="chat-bubble-retry" onClick={() => onRetry && onRetry(message)}>
                        重试
                    </div>
                )}
                <div className="chat-bubble-time">{message.time}</div>
            </div>
        </div>
    );
};

// ══════════════════════════════════════════════
//  加载动画
// ══════════════════════════════════════════════
const LoadingDots = () => (
    <div className="chat-loading">
        <span className="chat-loading-dot" />
        <span className="chat-loading-dot" />
        <span className="chat-loading-dot" />
    </div>
);

// ══════════════════════════════════════════════
//  主组件：GlobalChatAssistant
// ══════════════════════════════════════════════
const GlobalChatAssistant = () => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [sessionFull, setSessionFull] = useState(false);
    const messageListRef = useRef(null);

    // 自动滚动到底部
    useEffect(() => {
        if (messageListRef.current) {
            messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
        }
    }, [messages, loading]);

    // 格式化时间
    const formatTime = () => {
        const now = new Date();
        return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    };

    // 检查会话轮数
    const checkSessionLimit = useCallback((currentMessages) => {
        const userMsgCount = currentMessages.filter((m) => m.role === 'user').length;
        if (userMsgCount >= MAX_ROUNDS) {
            setSessionFull(true);
            setMessages((prev) => [
                ...prev,
                {
                    role: 'system',
                    content: `对话已达 ${MAX_ROUNDS} 轮上限，刷新页面可开始新会话`,
                    time: formatTime(),
                },
            ]);
            return true;
        }
        return false;
    }, []);

    // 发送消息
    const handleSend = useCallback(async (text) => {
        const trimmed = text?.trim();
        if (!trimmed || loading || sessionFull) return;

        const userMsg = {
            role: 'user',
            content: trimmed,
            time: formatTime(),
        };

        const newMessages = [...messages, userMsg];
        setMessages(newMessages);
        setInputValue('');

        // 检查轮数上限
        if (checkSessionLimit(newMessages)) return;

        setLoading(true);

        try {
            // 构建发送给后端的消息列表（不含 system 消息）
            const apiMessages = newMessages
                .filter((m) => m.role !== 'system')
                .map(({ role, content }) => ({ role, content }));

            const res = await sendChatMessage(apiMessages);

            if (res.code === 0) {
                setMessages((prev) => [
                    ...prev,
                    {
                        role: 'assistant',
                        content: res.data?.reply || '抱歉，我暂时无法回答这个问题。',
                        time: formatTime(),
                    },
                ]);
            } else {
                setMessages((prev) => [
                    ...prev,
                    {
                        role: 'assistant',
                        content: res.message || '服务繁忙，请稍后重试',
                        status: 'error',
                        time: formatTime(),
                    },
                ]);
            }
        } catch (err) {
            // 网络异常
            const errorContent = err?.message === 'Network Error'
                ? '网络连接失败，请检查网络后重试'
                : '请求超时，请重试';

            setMessages((prev) => [
                ...prev,
                {
                    role: 'assistant',
                    content: errorContent,
                    status: 'error',
                    time: formatTime(),
                    _retryContent: trimmed, // 保存重试时的原始内容
                },
            ]);
        } finally {
            setLoading(false);
        }
    }, [messages, loading, sessionFull, checkSessionLimit]);

    // 重试
    const handleRetry = (errorMsg) => {
        // 移除最后一条错误消息，重新发送
        setMessages((prev) => prev.slice(0, -1));
        handleSend(errorMsg._retryContent);
    };

    // 发送按钮点击
    const handleSendClick = () => {
        handleSend(inputValue);
    };

    // 键盘事件
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend(inputValue);
        }
    };

    // 切换抽屉
    const toggleDrawer = () => {
        setDrawerOpen((prev) => !prev);
    };

    const isInputEmpty = !inputValue.trim();
    const isInputDisabled = loading || sessionFull;

    return (
        <>
            {/* 悬浮按钮 */}
            <button
                className={`chat-float-btn ${drawerOpen ? 'drawer-open' : ''}`}
                onClick={toggleDrawer}
                title="AI 助手"
            >
                <CustomerServiceOutlined />
            </button>

            {/* 对话抽屉 */}
            <Drawer
                className="chat-drawer"
                title="AI 助手"
                placement="right"
                width={420}
                mask={false}
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                destroyOnClose={false}
                styles={{
                    body: { padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
                    header: { borderBottom: '1px solid #f0f0f0', padding: '14px 20px' },
                }}
            >
                {/* 消息列表 */}
                <div className="chat-message-list" ref={messageListRef}>
                    {messages.length === 0 && !loading ? (
                        <WelcomePanel />
                    ) : (
                        <>
                            {messages.map((msg, idx) => (
                                <MessageBubble key={idx} message={msg} onRetry={handleRetry} />
                            ))}
                            {loading && <LoadingDots />}
                        </>
                    )}
                </div>

                {/* 输入区 */}
                <div className="chat-input-area">
                    <TextArea
                        className="chat-input-textarea"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={sessionFull ? '会话已达上限' : '输入问题，Enter 发送，Shift+Enter 换行'}
                        disabled={isInputDisabled}
                        maxLength={MAX_CHARS}
                        autoSize={{ minRows: 2, maxRows: 6 }}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                        <Button
                            className="chat-input-send-btn"
                            type="primary"
                            icon={<SendOutlined />}
                            onClick={handleSendClick}
                            disabled={isInputEmpty || isInputDisabled}
                            shape="circle"
                        />
                        <span style={{
                            fontSize: 11,
                            color: inputValue.length > MAX_CHARS ? '#ff4d4f' : '#bbb',
                            whiteSpace: 'nowrap',
                        }}>
                            {inputValue.length}/{MAX_CHARS}
                        </span>
                    </div>
                </div>
            </Drawer>
        </>
    );
};

export default GlobalChatAssistant;
